/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React, { Fragment } from 'react';
import { Button, Table } from 'reactstrap';
import {
  FLOOR_PLAN_BY_STORY_FT_DIR_1,
  FLOOR_PLAN_BY_STORY_FT_DIR_2,
  FLOOR_PLAN_BY_STORY_LOCATION_SPECIFIER,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import { normalizeLocationSpecifierTokens } from '@hbrisk/sp3-risk-model-support/models/locationSpecifier/index.js';
import GridField from '#components/pages/Models/Model/sections/support/forms/GridField/index.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';

type Props = {|
  form: string,
  fields: {
    length: number,
    map: Function,
    push: Function,
    remove: Function,
  },
  meta: { error: string },
|};
const renderFloorPlanByStory = ({ form, fields, meta }: Props): Element<typeof Fragment> => {
  const { error } = meta;
  const allowDelete = fields.length > 1 || false;
  const floorPlanByStoryRows = fields.map((field, index) => (
    <tr key={field}>
      <td>
        <GridField
          name={`${field}.${FLOOR_PLAN_BY_STORY_LOCATION_SPECIFIER}`}
          form={form}
          options={{
            ComponentType: 'text',
            includeFormatting: false,
            format: normalizeLocationSpecifierTokens,
            normalize: normalizeLocationSpecifierTokens,
          }}
        />
      </td>
      <td>
        <GridField
          name={`${field}.${FLOOR_PLAN_BY_STORY_FT_DIR_1}`}
          form={form}
          options={{
            ComponentType: 'text',
            includeFormatting: false,
            inputGroup: {
              addonType: 'append',
              addonText: 'ft.',
            },
          }}
        />
      </td>
      <td>
        <GridField
          name={`${field}.${FLOOR_PLAN_BY_STORY_FT_DIR_2}`}
          form={form}
          options={{
            ComponentType: 'text',
            includeFormatting: false,
            inputGroup: {
              addonType: 'append',
              addonText: 'ft.',
            },
          }}
        />
      </td>
      <td>
        <Button color="link" onClick={() => fields.remove(index)} disabled={!allowDelete}>
          delete
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <Table responsive className="floor-plan-by-story-table">
        <thead>
          <tr>
            <th>
              Stories <Tip tipId="story" />
            </th>
            <th>Direction 1</th>
            <th>Direction 2</th>
          </tr>
        </thead>
        <tbody>{floorPlanByStoryRows}</tbody>
      </Table>
      {error && <div className="text-danger">{error}</div>}
      <Button
        size="sm"
        id="addFloorPlanRowButton"
        className="float-right"
        color="primary"
        onClick={() => fields.push({})}
      >
        Add Row
      </Button>
    </>
  );
};

export default renderFloorPlanByStory;
